import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Search, Plus, LayoutGrid, List as ListIcon, FileText, Edit3, Trash2, MoreVertical, Home as HomeIcon, User, MapPin, Euro, X, Download, ArrowLeft, Save, Ruler, Bed, Bath, Image as ImageIcon, RefreshCw, CheckCircle, AlertCircle, AlertTriangle, Send, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Helper Components ---
const Button = ({ children, variant = 'primary', icon: Icon, className = '', onClick, type = 'button', disabled = false }) => {
  const baseStyle = "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const variantStyles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-600/20 focus:ring-blue-500",
    secondary: "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm focus:ring-gray-500",
    ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-500",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500"
  };
  return (
    <button type={type} className={`${baseStyle} ${variantStyles[variant]} ${className}`} onClick={onClick} disabled={disabled}>
      {Icon && <Icon className={`w-4 h-4 ${children ? 'mr-2' : ''}`} />}
      {children}
    </button>
  );
};

const Input = ({ label, type = 'text', name, value, onChange, placeholder, icon: Icon, required = false, className = '' }) => {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all`}
        />
      </div>
    </div>
  );
};

const TextArea = ({ label, name, value, onChange, placeholder, rows = 4, required = false }) => {
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <textarea
        ref={textareaRef}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        required={required}
        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none overflow-hidden"
      />
    </div>
  );
};

const Select = ({ label, name, value, onChange, options, required = false }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
};

// --- Toast Notification Component ---
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: {
      bg: 'bg-green-50 border-green-200',
      icon: 'text-green-500',
      text: 'text-green-800'
    },
    error: {
      bg: 'bg-red-50 border-red-200',
      icon: 'text-red-500',
      text: 'text-red-800'
    }
  };

  const style = styles[type];
  const Icon = type === 'success' ? CheckCircle : AlertCircle;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg ${style.bg}`}
    >
      <Icon className={`w-5 h-5 ${style.icon}`} />
      <span className={`text-sm font-medium ${style.text}`}>{message}</span>
      <button onClick={onClose} className={`ml-2 ${style.icon} hover:opacity-70`}>
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(price);
};

// --- Edit Annonce Component ---
const EditAnnonce = ({ annonce, onBack, onSave }) => {
  const [formData, setFormData] = useState({
    reference: '',
    titre: '',
    texte: '',
    prix: '',
    date_saisie: '',
    code_type: '',
    code_postal_reel: '',
    code_postal_internet: '',
    ville_reelle: '',
    ville_internet: '',
    pays: '',
    quartier: '',
    surface: '',
    nb_pieces: '',
    nb_chambres: '',
    nb_salles_de_bain: '',
    surface_terrain: '',
    nb_wc: '',
    garage: '',
    nb_garages: '',
    terrasse: '',
    vente_prix_reel: '',
    contact_a_afficher: '',
    telephone_a_afficher: '',
    url_annonce_sur_site_annonceur: '',
    prix_vendu_net: '',
    photos: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    if (annonce) {
      setFormData(annonce);
      setHasChanges(false);
    }
  }, [annonce]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowExitModal(true);
    } else {
      onBack();
    }
  };

  const confirmExit = () => {
    setShowExitModal(false);
    onBack();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
      {/* Header Sticky */}
      <div className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur-sm py-4 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <div className="h-8 w-px bg-gray-300"></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {annonce ? "Modifier l'annonce" : "Nouvelle annonce"}
              </h1>
              <p className="text-sm text-gray-500">Référence: {formData.reference || 'Nouvelle'}</p>
            </div>
            {hasChanges && (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                Non sauvegardé
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle Actif/Inactif */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <span className={`text-sm font-medium ${formData.active !== false ? 'text-green-600' : 'text-gray-400'}`}>
                {formData.active !== false ? 'Actif' : 'Inactif'}
              </span>
              <button
                type="button"
                onClick={() => { setFormData(prev => ({ ...prev, active: prev.active === false ? true : false })); setHasChanges(true); }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.active !== false ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.active !== false ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="h-8 w-px bg-gray-200"></div>
            <Button variant="secondary" icon={X} onClick={handleBack}>
              Annuler
            </Button>
            <Button icon={Save} onClick={handleSubmit} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Images Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                Photo de l'annonce
              </h2>
              <p className="text-sm text-gray-500 mt-1">URL de la photo principale</p>
            </div>
          </div>
          <Input
            label="URL de la photo"
            name="photos"
            value={formData.photos}
            onChange={handleChange}
            placeholder="https://exemple.com/photo.jpg"
          />
          {formData.photos && (
            <div className="mt-4">
              <img
                src={formData.photos}
                alt="Aperçu"
                className="h-48 w-auto rounded-lg border border-gray-200 object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </motion.div>

        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <HomeIcon className="w-5 h-5 text-blue-600" />
            Informations générales
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Référence" name="reference" value={formData.reference} onChange={handleChange} required />
            <Input label="Code type" name="code_type" value={formData.code_type} onChange={handleChange} placeholder="Ex: 5200" />
            <Input label="Titre de l'annonce" name="titre" value={formData.titre} onChange={handleChange} placeholder="Ex: Belle villa moderne" required className="md:col-span-2" />
            <div className="md:col-span-2">
              <TextArea label="Description" name="texte" value={formData.texte} onChange={handleChange} placeholder="Décrivez le bien..." rows={6} />
            </div>
            <Input label="Date de saisie" type="date" name="date_saisie" value={formData.date_saisie} onChange={handleChange} />
          </div>
        </motion.div>

        {/* Location & Price */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Localisation & Prix
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Input label="Ville réelle" name="ville_reelle" value={formData.ville_reelle} onChange={handleChange} icon={MapPin} />
            <Input label="Ville internet" name="ville_internet" value={formData.ville_internet} onChange={handleChange} />
            <Input label="Code postal réel" name="code_postal_reel" value={formData.code_postal_reel} onChange={handleChange} />
            <Input label="Code postal internet" name="code_postal_internet" value={formData.code_postal_internet} onChange={handleChange} />
            <Input label="Pays" name="pays" value={formData.pays} onChange={handleChange} />
            <Input label="Quartier" name="quartier" value={formData.quartier} onChange={handleChange} />
            <Input label="Prix (€)" type="number" name="prix" value={formData.prix} onChange={handleChange} icon={Euro} required />
            <Input label="Prix réel de vente (€)" type="number" name="vente_prix_reel" value={formData.vente_prix_reel} onChange={handleChange} />
            <Input label="Prix vendu net (€)" type="number" name="prix_vendu_net" value={formData.prix_vendu_net} onChange={handleChange} />
          </div>
        </motion.div>

        {/* Property Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Ruler className="w-5 h-5 text-blue-600" />
            Caractéristiques du bien
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Input label="Surface (m²)" type="number" name="surface" value={formData.surface} onChange={handleChange} icon={Ruler} />
            <Input label="Surface terrain (m²)" type="number" name="surface_terrain" value={formData.surface_terrain} onChange={handleChange} />
            <Input label="Nombre de pièces" type="number" name="nb_pieces" value={formData.nb_pieces} onChange={handleChange} icon={HomeIcon} />
            <Input label="Chambres" type="number" name="nb_chambres" value={formData.nb_chambres} onChange={handleChange} icon={Bed} />
            <Input label="Salles de bain" type="number" name="nb_salles_de_bain" value={formData.nb_salles_de_bain} onChange={handleChange} icon={Bath} />
            <Input label="WC" type="number" name="nb_wc" value={formData.nb_wc} onChange={handleChange} />
            <Input label="Garage" name="garage" value={formData.garage} onChange={handleChange} />
            <Input label="Nombre de garages" type="number" name="nb_garages" value={formData.nb_garages} onChange={handleChange} />
            <Input label="Terrasse" name="terrasse" value={formData.terrasse} onChange={handleChange} />
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Contact & Liens
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Contact à afficher" name="contact_a_afficher" value={formData.contact_a_afficher} onChange={handleChange} />
            <Input label="Téléphone à afficher" name="telephone_a_afficher" value={formData.telephone_a_afficher} onChange={handleChange} />
            <Input label="URL de l'annonce" name="url_annonce_sur_site_annonceur" value={formData.url_annonce_sur_site_annonceur} onChange={handleChange} className="md:col-span-2" />
          </div>
        </motion.div>
      </form>

      {/* Modal de confirmation de sortie */}
      <AnimatePresence>
        {showExitModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setShowExitModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                    Modifications non sauvegardées
                  </h3>
                  <p className="text-gray-500 text-center text-sm">
                    Vous avez des modifications en cours. Si vous quittez maintenant, vos changements seront perdus.
                  </p>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
                  <button
                    onClick={() => setShowExitModal(false)}
                    className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Continuer l'édition
                  </button>
                  <button
                    onClick={confirmExit}
                    className="flex-1 px-4 py-2.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Quitter sans sauvegarder
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Main Home Component ---
function Home() {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAnnonces, setSelectedAnnonces] = useState([]);
  const [currentView, setCurrentView] = useState('grid');
  const [editingAnnonce, setEditingAnnonce] = useState(null);
  const [showXmlModal, setShowXmlModal] = useState(false);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [selectedForRefresh, setSelectedForRefresh] = useState([]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  useEffect(() => {
    fetchAnnonces();
  }, []);

  const fetchAnnonces = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/annonces`);
      setAnnonces(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/annonces/${id}`);
      setAnnonces(annonces.filter(a => a.id !== id));
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const handleToggleActive = async (annonce) => {
    const newActive = annonce.active === false ? true : false;
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/annonces/${annonce.id}`, {
        ...annonce,
        active: newActive
      });
      setAnnonces(annonces.map(a => a.id === annonce.id ? { ...a, active: newActive } : a));
      showToast(newActive ? 'Annonce activée' : 'Annonce désactivée', 'success');
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showToast('Erreur lors du changement de statut', 'error');
    }
  };

  const handleEditAnnonce = (annonce) => {
    setEditingAnnonce(annonce);
    setCurrentView('edit');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setEditingAnnonce(null);
  };

  const handleSaveAnnonce = async (formData) => {
    try {
      if (editingAnnonce) {
        const response = await axios.put(`${process.env.REACT_APP_API_URL}/annonces/${editingAnnonce.id}`, formData);
        setAnnonces(annonces.map(a => a.id === editingAnnonce.id ? response.data : a));
        showToast('Annonce mise à jour avec succès !', 'success');
      } else {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/annonces`, formData);
        setAnnonces([...annonces, response.data]);
        showToast('Annonce créée avec succès !', 'success');
      }
      handleBackToList();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde de l\'annonce', 'error');
    }
  };

  const handleGenerateXml = async () => {
    setXmlLoading(true);
    const apiUrl = `${process.env.REACT_APP_API_URL}/generate-and-send`;
    console.log('=== Appel API generate-and-send ===');
    console.log('URL:', apiUrl);
    try {
      const response = await axios.post(apiUrl);
      console.log('Réponse:', response.data);
      showToast('XML généré et envoyé avec succès !', 'success');
      setShowXmlModal(false);
    } catch (error) {
      console.error('Erreur lors de la génération XML:', error);
      console.error('Détails:', error.response?.data || error.message);
      showToast('Erreur lors de la génération XML', 'error');
    } finally {
      setXmlLoading(false);
    }
  };

  const handleRefreshAnnonces = async () => {
    if (selectedForRefresh.length === 0) {
      showToast('Veuillez sélectionner au moins une annonce', 'error');
      return;
    }
    setRefreshLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/refresh-annonce`, {
        annonceIds: selectedForRefresh
      });
      showToast(`${selectedForRefresh.length} annonce(s) rafraîchie(s) et envoyée(s) avec succès !`, 'success');
      fetchAnnonces();
      setShowRefreshModal(false);
      setSelectedForRefresh([]);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
      showToast('Erreur lors du rafraîchissement des annonces', 'error');
    } finally {
      setRefreshLoading(false);
    }
  };

  const toggleRefreshSelection = (id) => {
    setSelectedForRefresh(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllForRefresh = () => {
    const activeAnnonces = annonces.filter(a => a.active !== false);
    if (selectedForRefresh.length === activeAnnonces.length) {
      setSelectedForRefresh([]);
    } else {
      setSelectedForRefresh(activeAnnonces.map(a => a.id));
    }
  };

  const openRefreshModal = () => {
    setSelectedForRefresh([]);
    setShowRefreshModal(true);
  };

  const filteredAnnonces = useMemo(() => {
    return annonces.filter(a => {
      const matchesSearch =
        (a.titre?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (a.reference?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (a.ville_reelle?.toLowerCase() || '').includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [annonces, searchQuery]);

  const toggleSelection = (id) => {
    setSelectedAnnonces(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedAnnonces.length === filteredAnnonces.length) {
      setSelectedAnnonces([]);
    } else {
      setSelectedAnnonces(filteredAnnonces.map(a => a.id));
    }
  };

  const stats = useMemo(() => {
    const total = annonces.length;
    const avgPrice = annonces.length > 0
      ? Math.round(annonces.reduce((sum, a) => sum + (parseFloat(a.prix) || 0), 0) / annonces.length)
      : 0;
    return { total, avgPrice };
  }, [annonces]);

  if (currentView === 'edit') {
    return (
      <>
        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={closeToast} />
          )}
        </AnimatePresence>
        <EditAnnonce annonce={editingAnnonce} onBack={handleBackToList} onSave={handleSaveAnnonce} />
      </>
    );
  }

  return (
    <>
      {/* Toast Notifications */}
      <AnimatePresence>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={closeToast} />
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Page Header & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Liste des Annonces</h1>
          <p className="text-gray-500 mt-1">Gérez votre catalogue immobilier, vos prix et vos médias.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative group">
            <button
              onClick={openRefreshModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Forcer la mise à jour
              <Info className="w-4 h-4 opacity-70" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100]">
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
              Force le renvoi des annonces sélectionnées vers le portail,<br/>même si elles n'ont pas été modifiées.
            </div>
          </div>
          <Button variant="secondary" icon={Send} onClick={() => setShowXmlModal(true)}>
            Envoyer vers Ubiflow
          </Button>
          <Button icon={Plus} onClick={() => {
            setEditingAnnonce(null);
            setCurrentView('edit');
          }}>
            Nouvelle Annonce
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Annonces', value: stats.total, sub: 'Dans la base', color: 'blue' },
          { label: 'En Ligne', value: stats.total, sub: 'Actuellement visibles', color: 'green' },
          { label: 'Prix Moyen', value: formatPrice(stats.avgPrice), sub: 'Sur toutes les annonces', color: 'indigo' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col">
            <span className="text-gray-500 text-sm font-medium">{stat.label}</span>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                stat.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                stat.color === 'green' ? 'bg-green-50 text-green-600' :
                'bg-indigo-50 text-indigo-600'
              }`}>
                {stat.sub}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
          <div className="relative w-full sm:w-80 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Rechercher par référence, ville..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-all ${viewMode === 'list' ? 'bg-white shadow text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <ListIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-all ${viewMode === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement des annonces...</p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {viewMode === 'list' ? (
            <motion.div
              key="list"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 border-b border-gray-200">
                    <tr>
                      <th className="p-4 w-10">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={selectedAnnonces.length === filteredAnnonces.length && filteredAnnonces.length > 0}
                          onChange={selectAll}
                        />
                      </th>
                      <th className="px-6 py-3 font-semibold">Annonce</th>
                      <th className="px-6 py-3 font-semibold">Prix</th>
                      <th className="px-6 py-3 font-semibold">Localisation</th>
                      <th className="px-6 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAnnonces.map(item => (
                      <tr
                        key={item.id}
                        className="group hover:bg-blue-50/30 transition-colors cursor-pointer"
                        onClick={() => handleEditAnnonce(item)}
                      >
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedAnnonces.includes(item.id)}
                            onChange={() => toggleSelection(item.id)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="relative h-16 w-24 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                              {item.photos ? (
                                <img src={item.photos} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center text-gray-400">
                                  <ImageIcon className="w-6 h-6" />
                                </div>
                              )}
                            </div>
                            <div className="max-w-xs">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                  {item.reference}
                                </span>
                                {item.active === false && (
                                  <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded font-bold">
                                    Inactif
                                  </span>
                                )}
                              </div>
                              <p className={`font-medium line-clamp-2 group-hover:text-blue-600 transition-colors ${item.active === false ? 'text-gray-400' : 'text-gray-900'}`}>
                                {item.titre}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {formatPrice(item.prix || 0)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 text-gray-500">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{item.ville_reelle || 'Non définie'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleToggleActive(item)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${item.active !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                              title={item.active !== false ? 'Désactiver' : 'Activer'}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${item.active !== false ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                            <button
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              title="Modifier"
                              onClick={() => handleEditAnnonce(item)}
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title="Supprimer"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredAnnonces.length === 0 && (
                  <div className="p-12 text-center text-gray-500">
                    <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-lg font-medium text-gray-900">Aucune annonce trouvée</p>
                    <p className="text-sm mt-1">Essayez de modifier vos filtres ou votre recherche.</p>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Affichage de {filteredAnnonces.length} sur {annonces.length} résultats
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredAnnonces.map(item => (
                <div
                  key={item.id}
                  className={`group bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col cursor-pointer ${item.active === false ? 'border-red-200 opacity-60' : 'border-gray-200 hover:border-blue-200'}`}
                  onClick={() => handleEditAnnonce(item)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    {item.photos ? (
                      <img
                        src={item.photos}
                        alt={item.titre}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${item.active === false ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <ImageIcon className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className="bg-white/90 backdrop-blur px-2 py-1 rounded text-xs font-bold text-gray-900 shadow-sm">
                        {item.reference}
                      </span>
                      {item.active === false && (
                        <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold shadow-sm">
                          Inactif
                        </span>
                      )}
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleActive(item)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shadow-sm ${item.active !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                        title={item.active !== false ? 'Désactiver' : 'Activer'}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${item.active !== false ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                      <button
                        className="p-1.5 bg-white/90 backdrop-blur rounded-full text-gray-600 hover:text-red-500 transition-colors shadow-sm"
                        onClick={() => handleDelete(item.id)}
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 pt-12">
                      <p className="text-white font-bold text-lg">{formatPrice(item.prix || 0)}</p>
                    </div>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="font-medium text-gray-900 line-clamp-2 mb-1 group-hover:text-blue-600 transition-colors">
                        {item.titre}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        {item.ville_reelle || 'Non définie'}
                      </div>
                    </div>
                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex gap-2 text-xs text-gray-500">
                        {item.surface && <span className="bg-gray-50 px-2 py-1 rounded">{item.surface} m²</span>}
                        {item.nb_pieces && <span className="bg-gray-50 px-2 py-1 rounded">{item.nb_pieces} p.</span>}
                      </div>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          onClick={() => handleEditAnnonce(item)}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* XML Modal */}
      <AnimatePresence>
        {showXmlModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setShowXmlModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Send className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Envoyer vers Ubiflow</h2>
                      <p className="text-sm text-gray-500 mt-1">Publication des annonces sur le portail</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-700">
                      Vous allez envoyer toutes les annonces <strong>actives</strong> vers le portail Ubiflow.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Annonces actives à exporter:</span>
                      <span className="font-semibold text-gray-900">{annonces.filter(a => a.active !== false).length}</span>
                    </div>
                    {annonces.filter(a => a.active === false).length > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Annonces inactives (non exportées):</span>
                        <span className="font-semibold text-red-500">{annonces.filter(a => a.active === false).length}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Format:</span>
                      <span className="font-semibold text-gray-900">XML + ZIP</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Destination:</span>
                      <span className="font-semibold text-gray-900">Portail Ubiflow</span>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex items-center justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowXmlModal(false)}>
                    Annuler
                  </Button>
                  <Button icon={Send} onClick={handleGenerateXml} disabled={xmlLoading}>
                    {xmlLoading ? 'Envoi en cours...' : 'Envoyer'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Refresh Selection Modal */}
      <AnimatePresence>
        {showRefreshModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowRefreshModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Forcer la mise à jour</h3>
                    <p className="text-sm text-gray-500">Sélectionnez les annonces à rafraîchir</p>
                  </div>
                  <button onClick={() => setShowRefreshModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Select All */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedForRefresh.length === annonces.filter(a => a.active !== false).length && annonces.filter(a => a.active !== false).length > 0}
                      onChange={selectAllForRefresh}
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Tout sélectionner ({annonces.filter(a => a.active !== false).length} actives)
                    </span>
                  </label>
                  <span className="text-sm text-gray-500">
                    {selectedForRefresh.length} sélectionnée(s)
                  </span>
                </div>

                {/* Annonces List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {annonces.map(annonce => (
                    <div
                      key={annonce.id}
                      onClick={() => toggleRefreshSelection(annonce.id)}
                      className={`flex items-center gap-4 p-3 rounded-xl border cursor-pointer transition-all ${
                        selectedForRefresh.includes(annonce.id)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedForRefresh.includes(annonce.id)}
                        onChange={() => toggleRefreshSelection(annonce.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      />
                      {/* Image */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {annonce.photos && annonce.photos.length > 0 ? (
                          <img
                            src={Array.isArray(annonce.photos) ? annonce.photos[0] : annonce.photos}
                            alt={annonce.titre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ImageIcon className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-gray-500 font-medium">Réf: {annonce.reference || 'N/A'}</p>
                          {annonce.active === false && (
                            <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded font-bold">Inactif</span>
                          )}
                        </div>
                        <p className={`text-sm font-semibold truncate ${annonce.active === false ? 'text-gray-400' : 'text-gray-900'}`}>{annonce.titre || 'Sans titre'}</p>
                        <p className="text-sm font-bold text-blue-600">{formatPrice(annonce.prix || 0)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex items-center justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowRefreshModal(false)}>
                    Annuler
                  </Button>
                  <Button
                    icon={RefreshCw}
                    onClick={handleRefreshAnnonces}
                    disabled={refreshLoading || selectedForRefresh.length === 0}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {refreshLoading ? 'Envoi en cours...' : `Mettre à jour (${selectedForRefresh.length})`}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
    </>
  );
}

export default Home;
